import { useState, useEffect, useRef, useCallback } from 'react';
import type { AddressData, SetCurrentStore } from './types';
import { parseNewApiComponents, parseOldApiComponents } from './utils';

export function useAddressSearch(
  isLoaded: boolean,
  currentStore: AddressData,
  setCurrentStore: SetCurrentStore,
  onAddressSelect: (data: AddressData) => void,
) {
  const [searchValue, setSearchValue] = useState(currentStore.address || '');
  const [predictions, setPredictions] = useState<any[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [isLoadingPredictions, setIsLoadingPredictions] = useState(false);

  const predictionsRef = useRef<HTMLDivElement>(null);
  const userHasTyped = useRef(false);

  // Sync search input when parent address changes (e.g. on edit open)
  useEffect(() => {
    if (currentStore.address && currentStore.address !== searchValue) {
      setSearchValue(currentStore.address);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore.address]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (predictionsRef.current && !predictionsRef.current.contains(e.target as Node)) {
        setShowPredictions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleAddressSearchFallback = useCallback((query: string) => {
    if (!query || !isLoaded || !window.google?.maps?.places?.AutocompleteService) {
      setIsLoadingPredictions(false);
      return;
    }
    setIsLoadingPredictions(true);
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      { input: query, types: ['address'], componentRestrictions: { country: 'us' } },
      (results: any, status: any) => {
        setIsLoadingPredictions(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
          setShowPredictions(true);
        } else {
          setPredictions([]);
          setShowPredictions(false);
        }
      }
    );
  }, [isLoaded]);

  const handleAddressSearch = useCallback(async (query: string) => {
    if (!query || !isLoaded) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }
    setIsLoadingPredictions(true);
    try {
      if (window.google?.maps?.places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
        const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          includedPrimaryTypes: ['address'],
          locationRestriction: { country: 'us' },
        });
        if (suggestions?.length > 0) {
          setPredictions(suggestions);
          setShowPredictions(true);
        } else {
          setPredictions([]);
          setShowPredictions(false);
        }
      } else {
        handleAddressSearchFallback(query);
      }
    } catch {
      handleAddressSearchFallback(query);
    } finally {
      setIsLoadingPredictions(false);
    }
  }, [isLoaded, handleAddressSearchFallback]);

  // Debounced search — only runs after user has actually typed
  useEffect(() => {
    if (!userHasTyped.current) return;
    if (!searchValue) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }
    const id = setTimeout(() => handleAddressSearch(searchValue), 300);
    return () => clearTimeout(id);
  }, [searchValue, handleAddressSearch]);

  const handlePlaceSelect = useCallback(async (suggestion: any) => {
    try {
      const place = suggestion.toPlace();
      await place.fetchFields(['addressComponents', 'formattedAddress']);
      const components = parseNewApiComponents(place.addressComponents || []);
      let latitude = 0, longitude = 0;
      if (place.geometry?.location) {
        latitude = place.geometry.location.lat();
        longitude = place.geometry.location.lng();
      }
      const data: AddressData = {
        ...currentStore,
        address: place.formattedAddress || '',
        streetNumber: components.streetNumber || '',
        streetName: components.streetName || '',
        apartment: components.apartment || currentStore.apartment || '',
        city: components.city || '',
        state: components.state || 'NY',
        zipCode: components.zipCode || '',
        latitude,
        longitude,
      };
      onAddressSelect(data);
      setSearchValue(place.formattedAddress || '');
      setShowPredictions(false);
      setPredictions([]);
    } catch (err) {
      console.error('Error fetching place details:', err);
    }
  }, [currentStore, onAddressSelect]);

  const handlePlaceSelectFallback = useCallback((placeId: string) => {
    if (!window.google?.maps?.places?.PlacesService) return;
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails(
      { placeId, fields: ['address_components', 'formatted_address', 'geometry'] },
      (place: any, status: any) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          console.error('Failed to get place details:', status);
          return;
        }
        const components = parseOldApiComponents(place.address_components || []);
        let latitude = 0, longitude = 0;
        if (place.geometry?.location) {
          latitude = place.geometry.location.lat();
          longitude = place.geometry.location.lng();
        }
        const data: AddressData = {
          ...currentStore,
          address: place.formatted_address || '',
          streetNumber: components.streetNumber || '',
          streetName: components.streetName || '',
          apartment: components.apartment || currentStore.apartment || '',
          city: components.city || '',
          state: components.state || 'NY',
          zipCode: components.zipCode || '',
          latitude,
          longitude,
        };
        onAddressSelect(data);
        setSearchValue(place.formatted_address || '');
        setShowPredictions(false);
        setPredictions([]);
      }
    );
  }, [currentStore, onAddressSelect]);

  const handlePredictionClick = useCallback((prediction: any) => {
    if (typeof prediction.toPlace === 'function') {
      handlePlaceSelect(prediction);
    } else if (prediction.place_id) {
      handlePlaceSelectFallback(prediction.place_id);
    } else {
      console.error('Unknown prediction format:', prediction);
    }
  }, [handlePlaceSelect, handlePlaceSelectFallback]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    userHasTyped.current = true;
    setSearchValue(value);
    setCurrentStore((prev) => ({ ...prev, address: value }));
  }, [setCurrentStore]);

  const handleClearAddress = useCallback(() => {
    setSearchValue('');
    setShowPredictions(false);
    setPredictions([]);
    setCurrentStore((prev) => ({
      ...prev,
      address: '',
      streetNumber: '',
      streetName: '',
      city: '',
      state: 'NY',
      zipCode: '',
      latitude: 0,
      longitude: 0,
    }));
  }, [setCurrentStore]);

  const handleInputFocus = useCallback(() => {
    if (predictions.length > 0) setShowPredictions(true);
  }, [predictions.length]);

  return {
    searchValue,
    predictions,
    showPredictions,
    isLoadingPredictions,
    predictionsRef,
    handleInputChange,
    handleClearAddress,
    handlePredictionClick,
    handleInputFocus,
  };
}
